/**
 * InterviewQuestionEditor - Reusable editor for cascading interview questions.
 * Used at all three cascade levels: pipeline-stage, job-stage, and interview.
 *
 * Features:
 * - Inline editing with auto-save (no manual Save button)
 * - Drag-and-drop reordering via @dnd-kit/sortable
 * - AI refinement (optional)
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Trash2,
  Sparkles,
  Loader2,
  GripVertical,
  HelpCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type { PlannedQuestion } from '../services/recruitingApi';

const CATEGORIES = ['technical', 'behavioral', 'situational', 'cultural_fit', 'experience'] as const;
const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
const AUTO_SAVE_DELAY = 800;

interface InterviewQuestionEditorProps {
  questions: PlannedQuestion[];
  onSave: (questions: PlannedQuestion[]) => void;
  onAIRefine?: (questions: PlannedQuestion[]) => void;
  isRefining?: boolean;
  isSaving?: boolean;
  title?: string;
  readOnly?: boolean;
}

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: 'text-success bg-success/10 border-success/40',
  medium: 'text-warning bg-warning/10 border-warning/40',
  hard: 'text-destructive bg-destructive/10 border-destructive/40',
};

export function InterviewQuestionEditor({
  questions,
  onSave,
  onAIRefine,
  isRefining = false,
  isSaving = false,
  title,
  readOnly = false,
}: InterviewQuestionEditorProps) {
  const { t } = useTranslation('recruiting');
  const [localQuestions, setLocalQuestions] = useState<PlannedQuestion[]>(questions);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external changes
  const questionsKey = questions.map(q => q.id).join(',');
  const [prevKey, setPrevKey] = useState(questionsKey);
  if (questionsKey !== prevKey) {
    setPrevKey(questionsKey);
    setLocalQuestions(questions);
  }

  // Debounced auto-save
  const scheduleAutoSave = useCallback((updated: PlannedQuestion[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave(updated);
    }, AUTO_SAVE_DELAY);
  }, [onSave]);

  // Immediate save (for reorder, add, remove)
  const saveImmediately = useCallback((updated: PlannedQuestion[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    onSave(updated);
  }, [onSave]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const updateQuestion = (id: string, updates: Partial<PlannedQuestion>) => {
    const updated = localQuestions.map(q => q.id === id ? { ...q, ...updates } : q);
    setLocalQuestions(updated);
    scheduleAutoSave(updated);
  };

  const addQuestion = () => {
    const newId = uuidv4();
    const updated = [
      ...localQuestions,
      {
        id: newId,
        question: '',
        category: 'behavioral',
        guidance: '',
        difficulty: 'medium',
        source: 'manual' as const,
      },
    ];
    setLocalQuestions(updated);
    // Don't auto-save empty question — will save when user edits it
  };

  const removeQuestion = (id: string) => {
    const updated = localQuestions.filter(q => q.id !== id);
    setLocalQuestions(updated);
    saveImmediately(updated);
  };

  const handleAIRefine = () => {
    // Flush any pending edits before AI refine
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      onSave(localQuestions);
    }
    onAIRefine?.(localQuestions);
  };

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localQuestions.findIndex(q => q.id === active.id);
    const newIndex = localQuestions.findIndex(q => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const updated = [...localQuestions];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);
    setLocalQuestions(updated);
    saveImmediately(updated);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  {t('questions.plannedQuestionsTooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {title || t('questions.interviewQuestions')}
            {isSaving && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {onAIRefine && !readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAIRefine}
                disabled={isRefining}
              >
                {isRefining ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                {t('questions.aiRefine')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {localQuestions.length === 0 ? (
          <div className="text-center py-3 text-sm text-muted-foreground">
            {t('questions.noQuestions')}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localQuestions.map(q => q.id)}
              strategy={verticalListSortingStrategy}
            >
              {localQuestions.map((question) => (
                <SortableQuestionItem
                  key={question.id}
                  question={question}
                  readOnly={readOnly}
                  onChange={(updates) => updateQuestion(question.id, updates)}
                  onRemove={() => removeQuestion(question.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={addQuestion}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('questions.addQuestion')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function SortableQuestionItem({
  question,
  readOnly,
  onChange,
  onRemove,
}: {
  question: PlannedQuestion;
  readOnly: boolean;
  onChange: (updates: Partial<PlannedQuestion>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('recruiting');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id, disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-start gap-2 rounded-md border bg-card px-2 py-1.5',
        isDragging && 'opacity-50 shadow-lg',
      )}
    >
      {!readOnly && (
        <div
          className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none mt-1.5"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {readOnly ? (
        <>
          <span className="text-sm flex-1 min-w-0 truncate">{question.question}</span>
          <Badge variant="outline" className="text-xs shrink-0">
            {t(`questions.categories.${question.category}`)}
          </Badge>
          <Badge
            variant="secondary"
            className={cn('text-xs shrink-0', DIFFICULTY_COLORS[question.difficulty])}
          >
            {t(`questions.difficulties.${question.difficulty}`)}
          </Badge>
          {question.guidance && (
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
              {question.guidance}
            </span>
          )}
        </>
      ) : (
        <>
          <Textarea
            value={question.question}
            onChange={(e) => onChange({ question: e.target.value })}
            placeholder={t('questions.questionPlaceholder')}
            rows={2}
            className="flex-1 text-sm min-w-0 resize-none py-1"
          />
          <div className="flex flex-col gap-1 shrink-0 mt-0.5">
            <Select
              value={question.category}
              onValueChange={(val) => onChange({ category: val })}
            >
              <SelectTrigger className="h-7 text-xs w-[90px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {t(`questions.categories.${cat}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={question.difficulty}
              onValueChange={(val) => onChange({ difficulty: val })}
            >
              <SelectTrigger className="h-7 text-xs w-[90px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIFFICULTIES.map((diff) => (
                  <SelectItem key={diff} value={diff}>
                    {t(`questions.difficulties.${diff}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={question.guidance}
            onChange={(e) => onChange({ guidance: e.target.value })}
            placeholder={t('questions.guidancePlaceholder')}
            rows={2}
            className="flex-1 text-xs min-w-0 resize-none py-1"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive shrink-0 mt-1"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}
